import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, Lock, Plus, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { HelpHint } from './HelpHint';

const CHANNELS: Array<[string, string]> = [
  ['email', 'Email'],
  ['whatsapp', 'WhatsApp'],
  ['sms', 'SMS'],
];

/**
 * Your own details, edited by you.
 *
 * Adding a skill used to mean asking an admin to open the roster and type it
 * in, which meant it mostly did not happen — and a skill the system does not
 * know about is one the candidate ranking will never match, so a technician who
 * had just qualified quietly stopped being suggested for that work.
 *
 * The fields an admin controls are shown here too, greyed and labelled. Hiding
 * them would leave someone wondering why their pole certificate is not on the
 * form; showing them read-only answers the question without inviting an edit
 * the API would refuse anyway.
 */
export function MyProfile() {
  const queryClient = useQueryClient();
  const { data, isPending } = useQuery({ queryKey: ['my-profile'], queryFn: api.myProfile });

  const [form, setForm] = useState({
    name: '',
    phone: '',
    altPhone: '',
    email: '',
    locale: 'en',
    channels: [] as string[],
    skills: [] as string[],
  });
  const [newSkill, setNewSkill] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const profile = data?.profile;

  // Seeded once the record arrives. Without the guard, every refetch would
  // discard whatever the technician had half-typed.
  useEffect(() => {
    if (!profile) return;
    setForm({
      name: profile.name,
      phone: profile.phoneE164,
      altPhone: profile.altPhoneE164 ?? '',
      email: profile.email ?? '',
      locale: profile.locale,
      channels: profile.channels,
      skills: profile.skills,
    });
  }, [profile]);

  const save = useMutation({
    mutationFn: () =>
      api.updateMyProfile({
        name: form.name,
        phone: form.phone,
        altPhone: form.altPhone,
        email: form.email,
        locale: form.locale,
        channels: form.channels,
        skills: form.skills,
      }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      void queryClient.invalidateQueries({ queryKey: ['my-profile'] });
    },
    onError: (caught: Error) => setError(caught.message),
  });

  const addSkill = (skill: string) => {
    const trimmed = skill.trim();
    if (!trimmed || form.skills.includes(trimmed)) return;
    setForm((f) => ({ ...f, skills: [...f.skills, trimmed] }));
    setNewSkill('');
  };

  if (isPending) {
    return (
      <section className="card">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </section>
    );
  }

  if (!profile) {
    return (
      <section className="card">
        <h3 className="mb-2 font-heading text-lg">My profile</h3>
        <p className="text-sm text-muted-foreground">
          This account is not linked to a technician record, so there is nothing to edit here.
        </p>
      </section>
    );
  }

  // Suggestions the organisation already uses, minus the ones already picked.
  // Matching is on exact strings, so offering "fibre splicing" beats letting
  // someone type "fiber splicing" and never be matched again.
  const suggestions = (data?.knownSkills ?? []).filter((skill) => !form.skills.includes(skill));

  return (
    <section className="card">
      <h3 className="mb-1 flex items-center gap-1.5 font-heading text-lg">
        My profile
        <HelpHint>
          Your contact details and what you can do. Dispatch uses your skills to decide who to
          suggest for a job, so keeping them current means you get offered the right work.
        </HelpHint>
      </h3>
      <p className="mb-4 text-sm text-muted-foreground">
        Changes take effect immediately. Your phone number is where job links are sent.
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-sm font-medium">Name</span>
          <input
            className="input w-full"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium">Phone</span>
          <input
            className="input w-full"
            value={form.phone}
            inputMode="tel"
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium">Alternative phone</span>
          <input
            className="input w-full"
            value={form.altPhone}
            inputMode="tel"
            placeholder="Optional"
            onChange={(e) => setForm((f) => ({ ...f, altPhone: e.target.value }))}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium">Email</span>
          <input
            className="input w-full"
            type="email"
            value={form.email}
            placeholder="Optional"
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
        </label>
      </div>

      <fieldset className="mt-5">
        <legend className="mb-2 text-sm font-medium">Skills</legend>

        <ul className="flex flex-wrap gap-2">
          {form.skills.map((skill) => (
            <li key={skill}>
              <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 py-1 pl-3 pr-1 text-sm text-primary-800">
                {skill}
                <button
                  type="button"
                  onClick={() =>
                    setForm((f) => ({ ...f, skills: f.skills.filter((s) => s !== skill) }))
                  }
                  className="rounded-full p-0.5 hover:bg-primary-100"
                  aria-label={`Remove ${skill}`}
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </span>
            </li>
          ))}
          {form.skills.length === 0 && (
            <li className="text-sm text-muted-foreground">Nothing listed yet.</li>
          )}
        </ul>

        <div className="mt-3 flex gap-2">
          <input
            className="input flex-1"
            value={newSkill}
            placeholder="Add a skill, e.g. fibre splicing"
            onChange={(e) => setNewSkill(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                // Otherwise Enter submits the surrounding form and the skill is
                // lost along with everything else half-typed.
                e.preventDefault();
                addSkill(newSkill);
              }
            }}
          />
          <button type="button" className="btn-secondary" onClick={() => addSkill(newSkill)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Add
          </button>
        </div>

        {suggestions.length > 0 && (
          <p className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            Already used here:
            {suggestions.slice(0, 8).map((skill) => (
              <button
                key={skill}
                type="button"
                onClick={() => addSkill(skill)}
                className="rounded-full border border-border px-2 py-0.5 hover:bg-muted"
              >
                {skill}
              </button>
            ))}
          </p>
        )}
      </fieldset>

      <fieldset className="mt-5">
        <legend className="mb-2 text-sm font-medium">How to reach me</legend>
        <p className="mb-2 text-xs text-muted-foreground">
          Tried in this order until one gets through.
        </p>
        <div className="flex flex-wrap gap-2">
          {CHANNELS.map(([id, label]) => {
            const on = form.channels.includes(id);
            return (
              <button
                key={id}
                type="button"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    channels: on ? f.channels.filter((c) => c !== id) : [...f.channels, id],
                  }))
                }
                className={`rounded-lg border px-3 py-1.5 text-sm ${
                  on
                    ? 'border-primary-500 bg-primary-50 text-primary-800'
                    : 'border-border text-muted-foreground'
                }`}
                aria-pressed={on}
              >
                {label}
              </button>
            );
          })}
        </div>
      </fieldset>

      {/* Set by the office. Shown so nobody wonders where they went. */}
      <dl className="mt-5 grid grid-cols-2 gap-4 rounded-xl bg-muted/40 p-3 text-sm sm:grid-cols-4">
        {[
          ['Zones', profile.zones.length ? profile.zones.join(', ') : 'None set'],
          ['Jobs at once', String(profile.maxConcurrent)],
          [
            'Pole certificate',
            profile.certPoleExpiry
              ? `Expires ${new Date(profile.certPoleExpiry).toLocaleDateString()}`
              : 'Not recorded',
          ],
          ['On roster', profile.isActive ? 'Yes' : 'No'],
        ].map(([label, value]) => (
          <div key={label}>
            <dt className="flex items-center gap-1 text-xs text-muted-foreground">
              <Lock className="h-3 w-3" aria-hidden="true" />
              {label}
            </dt>
            <dd className="mt-0.5">{value}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-2 text-xs text-muted-foreground">
        These four are set by the office — coverage, workload and safety are dispatch decisions.
        Ask an administrator to change one.
      </p>

      {error && <p className="mt-4 text-sm text-danger-600">{error}</p>}

      <div className="mt-5 flex items-center gap-3">
        <button
          type="button"
          className="btn-primary"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Save changes
        </button>
        {saved && (
          <span className="flex items-center gap-1 text-sm text-secondary-700">
            <Check className="h-4 w-4" aria-hidden="true" />
            Saved
          </span>
        )}
      </div>
    </section>
  );
}
